import React, { useState, useEffect } from "react";
import {
  Layout,
  Button,
  Card,
  Row,
  Col,
  Typography,
  Modal,
  Form,
  Input,
  DatePicker,
  InputNumber,
  message,
  Spin,
  Rate,
  Statistic,
  Carousel,
  Space,
} from "antd";
import {
  EnvironmentOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  SafetyOutlined,
  TeamOutlined,
  CustomerServiceOutlined,
  HeartOutlined,
  ThunderboltOutlined,
  GlobalOutlined,
  RightOutlined,
  WhatsAppOutlined,
  FacebookOutlined,
  InstagramOutlined,
  TikTokOutlined,
  YoutubeOutlined,
  StarOutlined,
} from "@ant-design/icons";
import {
  packageAPI,
  bookingAPI,
  specialPackageAPI,
  testimonialAPI,
} from "../utils/api";
import {
  Package,
  BookingFormData,
  Destination,
  Testimonial,
  SpecialPackage,
  TestimonialFormData,
} from "../types";
import dayjs from "dayjs";
import "./HomePage.css";
import PureMasonryGallery from "./Puremasonrygallery";

const { Header, Content, Footer } = Layout;
const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

const HomePage: React.FC = () => {
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [customModalVisible, setCustomModalVisible] = useState<boolean>(false);
  const [bookingModalVisible, setBookingModalVisible] =
    useState<boolean>(false);
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [form] = Form.useForm<BookingFormData>();
  const [customForm] = Form.useForm<BookingFormData>();
  const [specialPackages, setSpecialPackages] = useState<SpecialPackage[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [testimonialModalVisible, setTestimonialModalVisible] =
    useState<boolean>(false);
  const [testimonialForm] = Form.useForm<TestimonialFormData>();

  const heroImages = [
    {
      url: "/images/carousel/colombo.jpg",
      title: "Temple of the Sacred Tooth Relic",
      description: "Lotus Tower rising above Colombo modern skyline",
    },
    {
      url: "/images/carousel/elephant.jpg",
      title: "Nine Arch Bridge, Ella",
      description: "Close-up of an elephant face and tusks",
    },
    {
      url: "/images/carousel/tea.jpg",
      title: "Nine Arch Bridge, Ella",
      description: "Marvel at engineering amidst tea plantations",
    },
    {
      url: "/images/carousel/thangalla.jpg",
      title: "Galle Fort",
      description: "Walk through colonial history by the sea",
    },
    {
      url: "/images/carousel/peacock.jpg",
      title: "Sigiriya Rock Fortress",
      description: "A vibrant peacock displaying its colorful feathers",
    },
  ];

  useEffect(() => {
    fetchPackages();
    fetchSpecialPackages();
    fetchTestimonials();
  }, []);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await packageAPI.getAll();
      setPackages(response.data.packages ?? []);
    } catch (error) {
      console.error("Error fetching packages:", error);
      message.error("Failed to load packages");
    } finally {
      setLoading(false);
    }
  };

  const fetchSpecialPackages = async () => {
    try {
      const response = await specialPackageAPI.getAll();
      setSpecialPackages(response.data.packages ?? []);
    } catch (error) {
      console.error("Error fetching special packages:", error);
      message.error("Failed to load special packages");
    }
  };

  const destinations: Destination[] = [
    {
      name: "Sigiriya Rock Fortress",
      image: "/images/Destinations/sigiriya.jpg",
      description:
        "An ancient rock fortress with stunning views and rich history",
      rating: 5,
    },
    {
      name: "Kandy Temple",
      image: "/images/Destinations/kandy.jpg",
      description: "Sacred Temple of the Tooth Relic in the cultural capital",
      rating: 4,
    },
    {
      name: "Mirissa",
      image: "/images/Destinations/mirissa.jpg",
      description:
        "Coconut Tree Hill overlooking the deep blue ocean in Mirissa.",
      rating: 5,
    },
    {
      name: "Ella Nine Arch Bridge",
      image: "/images/Destinations/ella.jpg",
      description: "Iconic railway bridge surrounded by lush tea plantations",
      rating: 5,
    },
    {
      name: "Pinnawala",
      image: "/images/Destinations/pinnawala.jpg",
      description:
        "Elephants bathing in the river at Pinnawala Elephant Orphanage",
      rating: 5,
    },
    {
      name: "Bentota",
      image: "/images/Destinations/bentota.jpg",
      description: "Aerial view of Bentota beach strip and river lagoon",
      rating: 5,
    },
    {
      name: "Udawalawa",
      image: "/images/Destinations/udawalawa.jpg",
      description: "Baby elephant standing near its herd in Udawalawe.",
      rating: 5,
    },
    {
      name: "Balapitiya",
      image: "/images/Destinations/balapitiya.jpg",
      description: "Madu River flowing through mangroves near Balapitiya.",
      rating: 5,
    },
  ];

  const features = [
    {
      icon: <TeamOutlined />,
      title: "Expert Local Guides",
      description:
        "Our experienced guides provide insider knowledge and ensure authentic experiences.",
    },
    {
      icon: <SafetyOutlined />,
      title: "Safety First",
      description:
        "Your safety is our priority with comprehensive insurance coverage.",
    },
    {
      icon: <CustomerServiceOutlined />,
      title: "24/7 Support",
      description:
        "Round-the-clock customer support for all your travel needs.",
    },
    {
      icon: <HeartOutlined />,
      title: "Personalized Experience",
      description:
        "Tailor-made itineraries to match your interests and preferences.",
    },
    {
      icon: <ThunderboltOutlined />,
      title: "Best Value",
      description:
        "Competitive pricing without compromising on quality or experience.",
    },
    {
      icon: <GlobalOutlined />,
      title: "Sustainable Tourism",
      description:
        "Eco-friendly practices supporting local communities and nature.",
    },
  ];

  const handleBookPackage = (pkg: Package) => {
    setSelectedPackage(pkg);
    setBookingModalVisible(true);
  };

  const handlePackageBooking = async (values: BookingFormData) => {
    if (!selectedPackage) return;

    try {
      const bookingData = {
        packageId: selectedPackage._id,
        packageName: selectedPackage.name,
        customerName: values.customerName,
        email: values.email,
        phone: values.phone,
        date: values.date.toISOString(),
        numberOfPeople: values.numberOfPeople,
        specialRequests: values.specialRequests || "",
        bookingType: "package" as const,
      };

      await bookingAPI.create(bookingData);
      message.success(
        "Booking request submitted successfully! We will contact you soon."
      );
      setBookingModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error("Error creating booking:", error);
      message.error("Failed to submit booking request");
    }
  };

  const fetchTestimonials = async () => {
    try {
      const response = await testimonialAPI.getAll();
      setTestimonials(response.data.testimonials ?? []);
    } catch (error) {
      console.error("Error fetching testimonials:", error);
      message.error("Failed to load testimonials");
    }
  };

  const handleTestimonialSubmit = async (values: TestimonialFormData) => {
    try {
      await testimonialAPI.create(values);
      message.success(
        "Thank you for your feedback! Your testimonial will be reviewed and published soon."
      );
      setTestimonialModalVisible(false);
      testimonialForm.resetFields();
    } catch (error) {
      console.error("Error submitting testimonial:", error);
      message.error("Failed to submit testimonial");
    }
  };

  const handleCustomBooking = async (values: BookingFormData) => {
    try {
      const bookingData = {
        packageName: "Custom Trip",
        customerName: values.customerName,
        email: values.email,
        phone: values.phone,
        date: values.date.toISOString(),
        numberOfPeople: values.numberOfPeople,
        destinations: values.destinations,
        specialRequests: values.specialRequests || "",
        bookingType: "custom" as const,
      };

      await bookingAPI.create(bookingData);
      message.success(
        "Custom trip request submitted successfully! We will contact you soon."
      );
      setCustomModalVisible(false);
      customForm.resetFields();
    } catch (error) {
      console.error("Error creating custom booking:", error);
      message.error("Failed to submit custom trip request");
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Layout className="home-layout">
      {/* Header */}
      <Header className="header">
        <div className="header-content">
          <div className="logo">
            <img
              src="/images/PradeepToursLogo.png"
              alt="Pradeep Tours Logo"
              style={{ height: "50px" }}
            />
            <Text strong className="title-text">
              Pradeep Tours
            </Text>
          </div>
          <nav className="nav-menu">
            <a href="#home">Home</a>
            <a href="#destinations">Destinations</a>
            <a href="#tours">Tours</a>
            <a href="#gallery">Gallery</a>
            <a href="#about">About Us</a>
            <a href="#contact">Contact</a>
          </nav>
          {/* <Button type="primary" size="large" href="/admin/login">
            Book Now
          </Button> */}
        </div>
      </Header>

      <Content>
        {/* Hero Section with Carousel */}
        <section className="hero-section" id="home">
          <Carousel
            autoplay
            autoplaySpeed={5000}
            effect="fade"
            className="hero-carousel"
          >
            {heroImages.map((image, index) => (
              <div key={index}>
                <div
                  className="hero-slide"
                  style={{
                    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${image.url})`,
                  }}
                >
                  <div className="hero-overlay">
                    <div className="hero-content">
                      <Title level={1} className="hero-title">
                        Explore Sri Lanka with Us
                      </Title>
                      <Paragraph className="hero-subtitle">
                        Discover the pearls of the Indian Ocean and let your
                        heart absorb the beauty, serenity, and adventure. Our
                        expertly curated tours bring you unforgettable
                        experiences in this tropical paradise.
                      </Paragraph>
                      <div className="hero-buttons">
                        <Button
                          type="primary"
                          size="large"
                          icon={<WhatsAppOutlined />}
                          className="hero-btn"
                          target="_blank"
                          href="https://wa.me/94710914907?text=Hi%2C%20I'm%20interested%20in%20booking%20a%20tour%20with%20Pradeep%20Tours.%20I%20saw%20your%20gallery%20and%20would%20like%20to%20know%20more!"
                        >
                          Contact Us on WhatsApp
                        </Button>
                        <Button
                          size="large"
                          icon={<CalendarOutlined />}
                          className="hero-btn-outline"
                          onClick={() =>
                            document
                              .getElementById("tours")
                              ?.scrollIntoView({ behavior: "smooth" })
                          }
                        >
                          Plan Your Trip
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </Carousel>
        </section>

        {/* Popular Tour Packages */}
        <section className="packages-section" id="tours">
          <div className="container">
            <div className="section-header">
              <Title level={2}>Popular Tour Packages</Title>
              <Paragraph>
                Carefully curated tours designed for both adventure lovers and
                cultural enthusiasts, offering a blend of history, nature, and
                relaxation.
              </Paragraph>
            </div>

            {packages?.length === 0 ? (
              <div style={{ textAlign: "center", padding: "50px" }}>
                <Paragraph>No packages available at the moment.</Paragraph>
              </div>
            ) : (
              <Row gutter={[24, 24]}>
                {packages?.map((pkg) => (
                  <Col xs={24} sm={12} lg={8} key={pkg._id}>
                    <Card
                      hoverable
                      className="package-card"
                      cover={
                        <div className="package-image">
                          <img
                            alt={pkg.name}
                            src={
                              pkg.image ||
                              "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=400"
                            }
                          />
                          <div className="package-price-tag">
                            {pkg.showPrice ? (
                              pkg.hasDiscount && pkg.discount > 0 ? (
                                <div>
                                  <div
                                    style={{
                                      textDecoration: "line-through",
                                      color: "rgba(255,255,255,0.7)",
                                      fontSize: "14px",
                                    }}
                                  >
                                    ${(pkg.price ?? 0).toFixed(2)}
                                  </div>
                                  <div>
                                    <Text
                                      strong
                                      style={{
                                        color: "white",
                                        fontSize: "20px",
                                      }}
                                    >
                                      ${(pkg.discountedPrice ?? 0).toFixed(2)}
                                    </Text>
                                    <Text
                                      style={{
                                        color: "white",
                                        fontSize: "12px",
                                      }}
                                    >
                                      {" "}
                                      / person
                                    </Text>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <Text
                                    strong
                                    style={{ color: "white", fontSize: "20px" }}
                                  >
                                    ${(pkg.price ?? 0).toFixed(2)}
                                  </Text>
                                  <Text
                                    style={{ color: "white", fontSize: "12px" }}
                                  >
                                    {" "}
                                    / person
                                  </Text>
                                </div>
                              )
                            ) : (
                              <div>
                                <Text
                                  strong
                                  style={{ color: "white", fontSize: "16px" }}
                                >
                                  Contact for Price
                                </Text>
                              </div>
                            )}
                          </div>

                          {/* Discount Badge */}
                          {pkg.showPrice && pkg.hasDiscount && pkg.discount > 0 && (
                            <div
                              style={{
                                position: "absolute",
                                top: "16px",
                                left: "16px",
                                background: "#ff4d4f",
                                color: "white",
                                padding: "4px 12px",
                                borderRadius: "4px",
                                fontWeight: "bold",
                                fontSize: "14px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                              }}
                            >
                              {pkg.discount}% OFF
                            </div>
                          )}
                        </div>
                      }
                    >
                      <Card.Meta
                        title={
                          <Text strong style={{ fontSize: "18px" }}>
                            {pkg.name}
                          </Text>
                        }
                        description={
                          <div>
                            <Paragraph
                              ellipsis={{ rows: 2 }}
                              className="package-description"
                            >
                              {pkg.description}
                            </Paragraph>

                            {/* Show savings if discount is applied */}
                            {pkg.showPrice && pkg.hasDiscount && pkg.discount > 0 && (
                              <div
                                style={{
                                  background: "#f6ffed",
                                  border: "1px solid #b7eb8f",
                                  borderRadius: "4px",
                                  padding: "8px",
                                  marginTop: "12px",
                                  marginBottom: "12px",
                                }}
                              >
                                <Text
                                  style={{
                                    color: "#52c41a",
                                    fontSize: "12px",
                                    fontWeight: "bold",
                                  }}
                                >
                                  💰 Save $
                                  {((pkg.price ?? 0) - (pkg.discountedPrice ?? 0)).toFixed(2)}{" "}
                                  per person!
                                </Text>
                              </div>
                            )}

                            {pkg.includes && pkg.includes.length > 0 && (
                              <div className="package-includes">
                                <Text
                                  type="secondary"
                                  style={{ fontSize: "12px" }}
                                >
                                  Tour Highlights:
                                </Text>
                                <ul>
                                  {pkg.includes.slice(0, 8).map((item, idx) => (
                                    <li key={idx}>
                                      <Text
                                        type="secondary"
                                        style={{ fontSize: "12px" }}
                                      >
                                        {item}
                                      </Text>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        }
                      />
                      <div className="package-button-book">
                        <Button
                          type="primary"
                          block
                          size="large"
                          onClick={() => handleBookPackage(pkg)}
                          icon={<CalendarOutlined />}
                        >
                          Book Now
                        </Button>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </div>
        </section>
        {/* special packages */}
        <section className="special-packages-section" id="sp-tours">
          <div className="container">
            {specialPackages.length > 0 && (
              <section
                className="packages-section"
                style={{ background: "#f5f7fa" }}
              >
                <div className="container">
                  <div className="section-header">
                    <Title level={2}>Special Multi-Day Tours</Title>
                    <Paragraph>
                      Extended adventures with carefully planned day-by-day
                      itineraries, including accommodations, meals, and expert
                      guidance throughout your journey.
                    </Paragraph>
                  </div>

                  <Row gutter={[24, 24]}>
                    {specialPackages?.map((pkg) => (
                      <Col xs={24} sm={12} lg={8} key={pkg._id}>
                        <Card
                          hoverable
                          className="package-card"
                          cover={
                            <div className="package-image">
                              <img
                                alt={pkg.name}
                                src={
                                  pkg.image ||
                                  "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=400"
                                }
                              />
                              <div className="package-price-tag">
                               {pkg?.showPrice ? (
                                    pkg?.hasDiscount && pkg.discount > 0 ? (
                                      <div>
                                        <div style={{ 
                                          textDecoration: 'line-through', 
                                          color: 'rgba(255,255,255,0.7)', 
                                          fontSize: '14px' 
                                        }}>
                                          From ${Math.min(pkg?.standardPrice ?? 0, pkg?.luxuryPrice ?? 0).toFixed(2)}
                                        </div>
                                        <div>
                                          <Text strong style={{ color: "white", fontSize: "16px" }}>
                                            From ${Math.min(pkg.discountedStandardPrice ?? 0, pkg.discountedLuxuryPrice ?? 0).toFixed(2)}
                                          </Text>
                                        </div>
                                      </div>
                                    ) : (
                                      <Text strong style={{ color: "white", fontSize: "16px" }}>
                                        From ${Math.min(pkg.standardPrice ?? 0, pkg.luxuryPrice ?? 0).toFixed(2)}
                                      </Text>
                                    )
                                  ) : (
                                    <div>
                                      <Text strong style={{ color: "white", fontSize: "16px" }}>
                                        Contact for Price
                                      </Text>
                                    </div>
                                  )}
                              </div>

                              {/* Duration Badge */}
                              <div className="package-duration package-detail-item">
                                <Text
                                  strong
                                  style={{ color: "white", fontSize: "12px" }}
                                >
                                  <ClockCircleOutlined /> {pkg.days} Days /{" "}
                                  {pkg.nights} Nights
                                </Text>
                              </div>

                              {/* ADDED: Discount Badge */}
                              {pkg.showPrice && pkg.hasDiscount && pkg.discount > 0 && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "16px",
                                    left: "16px",
                                    background: "#ff4d4f",
                                    color: "white",
                                    padding: "6px 12px",
                                    borderRadius: "4px",
                                    fontWeight: "bold",
                                    fontSize: "14px",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                                    zIndex: 1,
                                  }}
                                >
                                  {pkg.discount}% OFF
                                </div>
                              )}
                            </div>
                          }
                        >
                          <Card.Meta
                            title={
                              <Text strong style={{ fontSize: "18px" }}>
                                {pkg.name}
                              </Text>
                            }
                            description={
                              <div>
                                <Paragraph
                                  ellipsis={{ rows: 2 }}
                                  className="package-description"
                                >
                                  {pkg.description}
                                </Paragraph>

                                <div
                                  className="package-detail-item"
                                  style={{ marginTop: "12px" }}
                                >
                                  <EnvironmentOutlined /> {pkg.destination}
                                </div>

                                {pkg.showPrice && pkg.hasDiscount && pkg.discount > 0 && (
                                  <div
                                    style={{
                                      background: "#f6ffed",
                                      border: "1px solid #b7eb8f",
                                      borderRadius: "4px",
                                      padding: "8px",
                                      marginTop: "12px",
                                      marginBottom: "12px",
                                    }}
                                  >
                                    <Text
                                      style={{
                                        color: "#52c41a",
                                        fontSize: "12px",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      💰 Save up to $
                                      {Math.max(
                                        (pkg.standardPrice ?? 0) -
                                          (pkg.discountedStandardPrice ?? 0),
                                        (pkg.luxuryPrice ?? 0) -
                                          (pkg.discountedLuxuryPrice ?? 0)
                                      ).toFixed(2)}{" "}
                                      per person!
                                    </Text>
                                  </div>
                                )}

                                {pkg.includes && pkg.includes.length > 0 && (
                                  <div className="package-includes">
                                    <Text
                                      type="secondary"
                                      style={{ fontSize: "12px" }}
                                    >
                                      Highlights:
                                    </Text>
                                    <ul style={{ marginTop: "8px" }}>
                                      {pkg.includes.map((item, idx) => (
                                        <li key={idx}>
                                          <Text
                                            type="secondary"
                                            style={{ fontSize: "12px" }}
                                          >
                                            {item}
                                          </Text>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            }
                          />
                          <div
                            className="package-button-book"
                            style={{ marginTop: "12px" }}
                          >
                            <Button
                              type="primary"
                              block
                              size="large"
                              icon={<RightOutlined />}
                              href={`/tour/${pkg._id}`}
                            >
                              Show More
                            </Button>
                          </div>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>
              </section>
            )}
          </div>
        </section>

        {/* Top Destinations Section */}
        <section className="destinations-section" id="destinations">
          <div className="container">
            <div className="section-header">
              <Title level={2}>Top Destinations in Sri Lanka</Title>
              <Paragraph>
                Discover the most breathtaking locations that define Sri Lanka's
                beauty, culture, and heritage with coastal calls.
              </Paragraph>
            </div>

            <Row gutter={[24, 24]}>
              {destinations.map((dest, index) => (
                <Col xs={24} sm={12} lg={6} key={index}>
                  <Card
                    hoverable
                    className="destination-card"
                    cover={
                      <div className="destination-image">
                        <img alt={dest.name} src={dest.image} />
                        {/* <div className="image-overlay">
                          <Button
                            type="primary"
                            icon={<RightOutlined />}
                            onClick={() => message.info(`Explore ${dest.name}`)}
                          >
                            Explore
                          </Button>
                        </div> */}
                      </div>
                    }
                  >
                    <Card.Meta
                      title={<Text strong>{dest.name}</Text>}
                      description={
                        <div>
                          <Paragraph
                            ellipsis={{ rows: 2 }}
                            className="dest-description"
                          >
                            {dest.description}
                          </Paragraph>
                          <div className="dest-rating">
                            <Rate
                              disabled
                              defaultValue={dest?.rating}
                              allowHalf
                            />
                          </div>
                        </div>
                      }
                    />
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </section>

        <PureMasonryGallery />

        {/* Why Choose Section */}
        <section className="why-choose-section" id="about">
          <div className="container">
            <div className="section-header">
              <Title level={2}>Why Choose Sri Lanka Explorer?</Title>
              <Paragraph>
                We stand out with years of experience and commitment to
                providing an authentic and hassle-free travel experience that
                you'll cherish forever.
              </Paragraph>
            </div>

            <Row gutter={[32, 32]}>
              {features.map((feature, index) => (
                <Col xs={24} sm={12} lg={8} key={index}>
                  <Card className="feature-card" bordered={false}>
                    <div className="feature-icon">{feature.icon}</div>
                    <Title level={4}>{feature.title}</Title>
                    <Paragraph>{feature.description}</Paragraph>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </section>

        {/* Statistics Section */}
        <section className="stats-section">
          <div className="container">
            <Row gutter={[32, 32]} justify="center">
              <Col xs={12} sm={6}>
                <div className="stat-item">
                  <Statistic
                    title="Destinations"
                    value={15}
                    suffix="+"
                    valueStyle={{
                      color: "#1890ff",
                      fontSize: "48px",
                      fontWeight: "bold",
                    }}
                  />
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div className="stat-item">
                  <Statistic
                    title="Happy Tourists"
                    value={3000}
                    suffix="+"
                    valueStyle={{
                      color: "#52c41a",
                      fontSize: "48px",
                      fontWeight: "bold",
                    }}
                  />
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div className="stat-item">
                  <Statistic
                    title="Tour Packages"
                    value={12}
                    suffix="+"
                    valueStyle={{
                      color: "#faad14",
                      fontSize: "48px",
                      fontWeight: "bold",
                    }}
                  />
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div className="stat-item">
                  <Statistic
                    title="Average Rating"
                    value={4.9}
                    valueStyle={{
                      color: "#f5222d",
                      fontSize: "48px",
                      fontWeight: "bold",
                    }}
                  />
                </div>
              </Col>
            </Row>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="testimonials-section">
          <div className="container">
            <div className="section-header">
              <Title level={2} style={{ color: "white" }}>
                What Our Travelers Say
              </Title>
              <Paragraph style={{ color: "rgba(255,255,255,0.8)" }}>
                Don't just take our word for it. Here's what our happy travelers
                have to say about their Sri Lankan adventure with us.
              </Paragraph>
            </div>

            {testimonials.length === 0 && (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <Paragraph style={{ color: "rgba(255,255,255,0.6)" }}>
                  No testimonials yet. Be the first to share your experience!
                </Paragraph>
              </div>
            )}
            <Carousel autoplay dots className="testimonial-carousel">
              {testimonials.map((testimonial, index) => (
                <div key={index}>
                  <Card className="testimonial-card">
                    <div className="testimonial-rating">
                      <Rate disabled defaultValue={testimonial.rating} />
                    </div>
                    <Paragraph className="testimonial-comment">
                      "{testimonial.comment}"
                    </Paragraph>
                    <div className="testimonial-author">
                      <div>
                        <Text strong>{testimonial.name}</Text>
                        <br />
                        <Text type="secondary">{testimonial.role}</Text>
                      </div>
                    </div>
                  </Card>
                </div>
              ))}
            </Carousel>
            <div style={{ textAlign: "center", marginTop: "40px" }}>
              <Button
                type="primary"
                size="large"
                icon={<StarOutlined />}
                onClick={() => setTestimonialModalVisible(true)}
                style={{
                  background: "linear-gradient(135deg, #1890ff, #722ed1)",
                  border: "none",
                }}
              >
                Share Your Experience
              </Button>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <div className="cta-overlay">
            <div className="container">
              <Title level={2} style={{ color: "white", marginBottom: "16px" }}>
                Ready for Your Sri Lankan Adventure?
              </Title>
              <Paragraph
                style={{
                  color: "rgba(255,255,255,0.9)",
                  fontSize: "18px",
                  marginBottom: "32px",
                }}
              >
                Join thousands of satisfied travelers who have experienced the
                magic of Sri Lanka with us. Your unforgettable journey awaits!
              </Paragraph>
              <div className="cta-buttons">
                <Button
                  type="primary"
                  size="large"
                  icon={<CalendarOutlined />}
                  onClick={() =>
                    document
                      .getElementById("tours")
                      ?.scrollIntoView({ behavior: "smooth" })
                  }
                  style={{ marginRight: "16px" }}
                >
                  Book Your Trip Now
                </Button>
                <Button
                  size="large"
                  style={{ background: "white", color: "#1890ff" }}
                  target="_blank"
                  href="https://wa.me/94710914907?text=Hi%2C%20I'm%20interested%20in%20booking%20a%20tour%20with%20Pradeep%20Tours.%20I%20saw%20your%20gallery%20and%20would%20like%20to%20know%20more!"
                >
                  Get Free Consultation
                </Button>
              </div>

              {/* <div className="newsletter-section">
                <Title level={4} style={{ color: "white", marginTop: "48px" }}>
                  Stay Updated with Travel Tips & Offers
                </Title>
                <div className="newsletter-form">
                  <Input
                    size="large"
                    placeholder="Enter your email address"
                    prefix={<MailOutlined />}
                    style={{ maxWidth: "400px", marginRight: "16px" }}
                  />
                  <Button type="primary" size="large">
                    Subscribe
                  </Button>
                </div>
              </div> */}
            </div>
          </div>
        </section>
      </Content>

      {/* Footer */}
      <Footer className="footer" id="contact">
        <div className="container">
          <Row gutter={[32, 32]}>
            <Col xs={24} sm={12} lg={6}>
              <Title
                level={4}
                className="title-text"
                style={{
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <img
                  src="/images/PradeepToursLogo.png"
                  alt="Pradeep Tours Logo"
                  style={{ height: "96px" }}
                />{" "}
                Pradeep Tours
              </Title>
              <Paragraph style={{ color: "rgba(255,255,255,0.7)" }}>
                Your trusted partner for discovering the wonders of Sri Lanka.
                Creating memories that last a lifetime.
              </Paragraph>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Title level={5} style={{ color: "white" }}>
                Quick Links
              </Title>
              <ul className="footer-links">
                <li>
                  <a href="#home">Home</a>
                </li>
                <li>
                  <a href="#destinations">Top Destinations</a>
                </li>
                <li>
                  <a href="#tours">Tour Packages</a>
                </li>
                <li>
                  <a href="#gallery">Gallery</a>
                </li>
                <li>
                  <a href="#about">About Us</a>
                </li>
                <li>
                  <a href="/admin/login">Contact Us</a>
                </li>
              </ul>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Title level={5} style={{ color: "white" }}>
                Popular Destinations
              </Title>
              <ul className="footer-links">
                <li>
                  <a href="#destinations">Kandy</a>
                </li>
                <li>
                  <a href="#destinations">Galle</a>
                </li>
                <li>
                  <a href="#destinations">Sigiriya</a>
                </li>
                <li>
                  <a href="#destinations">Ella</a>
                </li>
                <li>
                  <a href="#destinations">Colombo</a>
                </li>
              </ul>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Title level={5} style={{ color: "white" }}>
                Contact Info
              </Title>
              <ul className="footer-links">
                <li>
                  <PhoneOutlined /> +94 71 091 4907
                </li>
                <li>
                  <WhatsAppOutlined /> +61 41 548 1461
                </li>
                <li>
                  <MailOutlined /> pradeeptours.info@gmail.com
                </li>
              </ul>
              <Title level={5} style={{ color: "white", marginTop: "24px" }}>
                Follow Us
              </Title>
              <div
                style={{
                  display: "flex",
                  gap: "16px",
                  fontSize: "24px",
                  marginTop: "12px",
                }}
              >
                <a
                  href="https://www.facebook.com/profile.php?id=61555457259273&mibextid=LQQJ4d"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "rgba(255,255,255,0.7)",
                    transition: "color 0.3s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.color = "#1877f2")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.color = "rgba(255,255,255,0.7)")
                  }
                >
                  <FacebookOutlined />
                </a>
                <a
                  href="https://www.instagram.com/pradeep_tours_srilanka_/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "rgba(255,255,255,0.7)",
                    transition: "color 0.3s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.color = "#E4405F")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.color = "rgba(255,255,255,0.7)")
                  }
                >
                  <InstagramOutlined />
                </a>
                <a
                  href="https://www.tiktok.com/@pradeeptours"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "rgba(255,255,255,0.7)",
                    transition: "color 0.3s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.color = "#00f2ea")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.color = "rgba(255,255,255,0.7)")
                  }
                >
                  <TikTokOutlined />
                </a>
                <a
                  href="https://www.youtube.com/@PradeepToursSriLanka"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "rgba(255,255,255,0.7)",
                    transition: "color 0.3s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.color = "#E4405F")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.color = "rgba(255,255,255,0.7)")
                  }
                >
                  <YoutubeOutlined />
                </a>
              </div>
            </Col>
          </Row>
          <div className="footer-bottom">
            <Text style={{ color: "rgba(255,255,255,0.7)" }}>
              © 2025 Pradeep Tours Sri Lanka. All rights reserved. | Designed by
              VeloxEngine
            </Text>
          </div>
        </div>
      </Footer>

      {/* Package Booking Modal */}
      <Modal
        title={`Book ${selectedPackage?.name}`}
        open={bookingModalVisible}
        onCancel={() => {
          setBookingModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handlePackageBooking}>
          <Form.Item
            name="customerName"
            label="Full Name"
            rules={[{ required: true, message: "Please enter your name" }]}
          >
            <Input prefix={<UserOutlined />} placeholder="Your name" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Please enter your email" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="your@email.com" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Phone Number"
            rules={[
              { required: true, message: "Please enter your phone number" },
            ]}
          >
            <Input prefix={<PhoneOutlined />} placeholder="+94771234567" />
          </Form.Item>

          <Form.Item
            name="date"
            label="Trip Date"
            rules={[{ required: true, message: "Please select a date" }]}
          >
            <DatePicker
              style={{ width: "100%" }}
              disabledDate={(current) =>
                current && current < dayjs().startOf("day")
              }
              format="MMMM DD, YYYY"
            />
          </Form.Item>

          <Form.Item
            name="numberOfPeople"
            label="Number of People"
            rules={[
              { required: true, message: "Please enter number of people" },
            ]}
            initialValue={1}
          >
            <InputNumber min={1} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="specialRequests" label="Special Requests (Optional)">
            <TextArea
              rows={3}
              placeholder="Any special requirements or preferences"
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block size="large">
              Submit Booking Request
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Custom Trip Modal */}
      <Modal
        title="Plan Your Custom Trip"
        open={customModalVisible}
        onCancel={() => {
          setCustomModalVisible(false);
          customForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={customForm}
          layout="vertical"
          onFinish={handleCustomBooking}
        >
          <Form.Item
            name="customerName"
            label="Full Name"
            rules={[{ required: true, message: "Please enter your name" }]}
          >
            <Input prefix={<UserOutlined />} placeholder="Your name" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Please enter your email" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="your@email.com" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Phone Number"
            rules={[
              { required: true, message: "Please enter your phone number" },
            ]}
          >
            <Input prefix={<PhoneOutlined />} placeholder="+94771234567" />
          </Form.Item>

          <Form.Item
            name="destinations"
            label="Destinations"
            rules={[
              {
                required: true,
                message: "Please enter your desired destinations",
              },
            ]}
          >
            <Input placeholder="e.g., Kandy, Galle, Ella" />
          </Form.Item>

          <Form.Item
            name="date"
            label="Trip Date"
            rules={[{ required: true, message: "Please select a date" }]}
          >
            <DatePicker
              style={{ width: "100%" }}
              disabledDate={(current) =>
                current && current < dayjs().startOf("day")
              }
              format="MMMM DD, YYYY"
            />
          </Form.Item>

          <Form.Item
            name="numberOfPeople"
            label="Number of People"
            rules={[
              { required: true, message: "Please enter number of people" },
            ]}
            initialValue={1}
          >
            <InputNumber min={1} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="specialRequests" label="Special Requests (Optional)">
            <TextArea
              rows={3}
              placeholder="Tell us about your preferences, interests, budget, etc."
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block size="large">
              Submit Custom Trip Request
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* testamonial Modal */}
      <Modal
        title="Share Your Travel Experience"
        open={testimonialModalVisible}
        onCancel={() => {
          setTestimonialModalVisible(false);
          testimonialForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Paragraph style={{ marginBottom: 24 }}>
          We'd love to hear about your experience with Pradeep Tours! Your
          feedback helps us improve and assists future travelers.
        </Paragraph>

        <Form
          form={testimonialForm}
          layout="vertical"
          onFinish={handleTestimonialSubmit}
        >
          <Form.Item
            name="name"
            label="Your Name"
            rules={[{ required: true, message: "Please enter your name" }]}
          >
            <Input prefix={<UserOutlined />} placeholder="John Doe" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Please enter your email" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="your@email.com" />
          </Form.Item>

          <Form.Item name="role" label="You are a..." initialValue="Traveler">
            <Input placeholder="e.g., Adventure Enthusiast, Family Traveler" />
          </Form.Item>

          <Form.Item
            name="tourPackage"
            label="Which tour did you take? (Optional)"
          >
            <Input placeholder="e.g., Cultural Triangle Explorer" />
          </Form.Item>

          <Form.Item
            name="rating"
            label="Overall Rating"
            rules={[{ required: true, message: "Please rate your experience" }]}
          >
            <Rate />
          </Form.Item>

          <Form.Item
            name="comment"
            label="Your Experience"
            rules={[
              { required: true, message: "Please share your experience" },
              { max: 500, message: "Comment must be less than 500 characters" },
            ]}
          >
            <TextArea
              rows={5}
              placeholder="Tell us about your experience with Pradeep Tours..."
              showCount
              maxLength={500}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<StarOutlined />}>
                Submit Testimonial
              </Button>
              <Button
                onClick={() => {
                  setTestimonialModalVisible(false);
                  testimonialForm.resetFields();
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default HomePage;
